import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faMicrophone,
  faMicrophoneSlash,
  faForwardStep
} from "@fortawesome/free-solid-svg-icons";
import "react-toastify/dist/ReactToastify.css";
import SpeakerOnIcon from "./icons/SpeakerOnIcon";
import SpeakerOffIcon from "./icons/SpeakerOffIcon";
import ChatHistoryIcon from "./icons/ChatHistoryIcon";
import SendChatIcon from "./icons/SendChatIcon";
import CaptionIcon from "./icons/CaptionIcon";

function LMSActivityStyle({
  char,
  handleStopTTS,
  displayedText,
  initialMessage,
  question,
  inputRef,
  handleInputChange,
  handleKeyDown,
  handleInputFocus,
  handleSendMessage,
  isLoading,
  isTyping,
  handleSkipTyping,
  messagesContainerRef,
  session_id,
  class_id,
  isTTSEnabled,
  setIsTTSEnabled,
  handleVoiceInput,
  isListening,
  speakHoverText,
}) {
  const [placeholderText, setPlaceholderText] = useState("Ask me a question! 😊");
  const placeholderOptions = [
    "Ask me a question! 😊",
    "Say something fun! 🤔",
    "What do you want to know? 💫",
    "Let's get started! 🚀",
    "I'm here to help! 😊",
  ];
  const [showStartMessage, setShowStartMessage] = useState(true);
  const [messageReceived, setMessageReceived] = useState(false);
  const [selectedOptionKey, setSelectedOptionKey] = useState(null);
  const [startButtonSelected, setStartButtonSelected] = useState(false);
  
  // New states for voice input auto-send functionality
  const listeningTimerRef = useRef(null);
  const listeningStartTimeRef = useRef(null);
  const [autoVoiceSendEnabled, setAutoVoiceSendEnabled] = useState(false);
  
  // Add a ref to track if TTS has been initialized
  const ttsInitializedRef = useRef(false);
  // Track if initial message has been sent
  const initialMessageSentRef = useRef(false);

  // Determine if voice input should be allowed
  const shouldAllowVoiceInput = displayedText && 
                               !isTyping && 
                               !isLoading && 
                               !(isTyping && messageReceived) &&
                               !showStartMessage;

  // Preload TTS on component mount
  useEffect(() => {
    // Initialize TTS with a silent utterance on page load
    const preloadTTS = () => {
      if (!ttsInitializedRef.current && window.speechSynthesis) {
        // Create an empty utterance to initialize the speech engine
        const utterance = new SpeechSynthesisUtterance("");
        utterance.volume = 0; // Make it silent
        utterance.rate = 0.85;
        
        // Initialize the engine
        window.speechSynthesis.speak(utterance);
        
        // Cancel it immediately (we just need to initialize)
        window.speechSynthesis.cancel();
        
        ttsInitializedRef.current = true;
        console.log("TTS engine preloaded");
      }
    };
    
    // Try to preload after a small delay to ensure the page is fully loaded
    setTimeout(preloadTTS, 500);
    
    // Also try during user interaction (scroll, mousemove) before they click start
    const handleUserInteraction = () => {
      if (!ttsInitializedRef.current) {
        preloadTTS();
      }
    };
    
    // Add event listeners for common user interactions
    document.addEventListener('scroll', handleUserInteraction, { passive: true, once: true });
    document.addEventListener('mousemove', handleUserInteraction, { passive: true, once: true });
    
    return () => {
      document.removeEventListener('scroll', handleUserInteraction);
      document.removeEventListener('mousemove', handleUserInteraction);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderText((prev) => {
        const currentIndex = placeholderOptions.indexOf(prev);
        const nextIndex = (currentIndex + 1) % placeholderOptions.length;
        return placeholderOptions[nextIndex];
      });
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (
      displayedText &&
      !displayedText.includes(
        "Message from System: start the user over from the beginning"
      )
    ) {
      setShowStartMessage(false);
      setMessageReceived(true);
      setSelectedOptionKey(null);
    }
  }, [displayedText, isTTSEnabled]);

  // Scroll to bottom when a new message appears
  useEffect(() => {
    if (messagesContainerRef?.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [displayedText]);

  // Track the last time the question text changed
  const lastTextChangeTimeRef = useRef(Date.now());
  
  // Listen for question changes and update the lastTextChangeTime
  useEffect(() => {
    if (isListening) {
      lastTextChangeTimeRef.current = Date.now();
    }
  }, [question, isListening]);
  
  // Force stop listening function reference
  const forceStopListeningRef = useRef(null);

  // Store the original handleVoiceInput in a ref
  const originalHandleVoiceInputRef = useRef(handleVoiceInput);
  
  // Update the ref when the prop changes
  useEffect(() => {
    originalHandleVoiceInputRef.current = handleVoiceInput;
  }, [handleVoiceInput]);

  // This effect actively stops listening when conditions change
  useEffect(() => {
    // If listening is active but conditions don't allow it anymore, force stop
    if (isListening && !shouldAllowVoiceInput) {
      // Call the voice input handler to toggle off listening
      if (forceStopListeningRef.current) {
        forceStopListeningRef.current();
      }
    }
  }, [isListening, shouldAllowVoiceInput, isTyping, isLoading, displayedText]);

  // New effect for voice input auto-send functionality
  useEffect(() => {
    // Only start timer when listening is allowed and active
    if (isListening && shouldAllowVoiceInput) {
      if (!listeningStartTimeRef.current) {
        listeningStartTimeRef.current = Date.now();
        lastTextChangeTimeRef.current = Date.now(); // Initialize when starting to listen
      }
      
      listeningTimerRef.current = setInterval(() => {
        // Calculate time since last text change
        const timeSinceLastTextChange = Date.now() - lastTextChangeTimeRef.current;
        
        // Only auto-send if there has been no text change for 4+ seconds AND there's text
        if (timeSinceLastTextChange >= 4000 && question.trim() !== "") {
          setAutoVoiceSendEnabled(true);
          handleInputSendMessage();
          clearInterval(listeningTimerRef.current);
        }
      }, 300); // Check frequently but not too much
    } else {
      // Clear timer when not listening or when conditions change
      if (listeningTimerRef.current) {
        clearInterval(listeningTimerRef.current);
        listeningTimerRef.current = null;
      }
      listeningStartTimeRef.current = null;
    }
  
    // Auto-restart listening after typing completes if auto voice was enabled
    if (autoVoiceSendEnabled && shouldAllowVoiceInput && !isListening) {
      setAutoVoiceSendEnabled(false);
      // Small delay to ensure the previous response is fully processed
      setTimeout(() => {
        if (shouldAllowVoiceInput) {
          // Only auto-restart if conditions still allow
          originalHandleVoiceInputRef.current();
        }
      }, 500);
    }

    
  
    return () => {
      if (listeningTimerRef.current) {
        clearInterval(listeningTimerRef.current);
      }
    };
  }, [isListening, question, isLoading, isTyping, autoVoiceSendEnabled, displayedText, shouldAllowVoiceInput]);
  

  // Updated TTS button to also stop TTS and skip typing
  const handleTTSButtonClick = () => {
    handleStopTTS();
    handleSkipTyping();
    setIsTTSEnabled((prev) => !prev);
  };

  const extractOptions = (text) => {
    // Skip option extraction for gold character
    if (char === "gold") {
      return { options: {}, modifiedText: text };
    }
    
    const options = {};
    const modifiedText = text.replace(
      /([1-9])\)\s*(.*?)(?=(?:[1-9]\)|$))/g,
      (match, option, answer) => {
        const sanitizedAnswer = String(answer).replace(/[?,]/g, "").trim();
        options[option] = sanitizedAnswer;
        return "";
      }
    );
    return { options, modifiedText: modifiedText.trim() };
  };

  // Handle fast-forward button (skip TTS and typing)
  const handleFastForward = () => {
    handleStopTTS();
    handleSkipTyping();
  };

  const { options, modifiedText } = extractOptions(displayedText || "");

  const handleActivityButtonClick = (value) => {
    if (!isLoading && !isTyping) {
      handleInputChange({ target: { value } });
      handleSendMessage(value);
      if (isTTSEnabled) console.log("TTS Start: ", value);
      setMessageReceived(false);
    }
  };

  const handleInputSendMessage = () => {
    handleSendMessage();
    if (isTTSEnabled) console.log("TTS Start: ", question);
    setMessageReceived(false);
  };

  // Custom voice input handler that only activates when allowed
  // and provides a way to forcibly stop listening
  const handleSafeVoiceInput = () => {
    if (shouldAllowVoiceInput) {
      handleVoiceInput();
    }
  };

  // Function to forcibly stop listening
  const forceStopVoiceInput = () => {
    // Only need to call the handler if currently listening
    if (isListening) {
      handleVoiceInput();
    }
  };

  // Store the force stop function in the ref so effects can access it
  useEffect(() => {
    forceStopListeningRef.current = forceStopVoiceInput;
  }, [isListening]);

  useEffect(() => {
    if (isListening) {
      const inactivityTimeout = setTimeout(() => {
        if (forceStopListeningRef.current) {
          forceStopListeningRef.current();
        }
      }, 90000); // 90,000 ms = 1.5 minutes
      return () => clearTimeout(inactivityTimeout);
    }
  }, [question, isListening]);

  useEffect(() => {
    const preloadCurrentCharacterGifs = () => {
      const currentChar = gifMap[char] || gifMap["default"];
      [currentChar.typing, currentChar.idle].forEach((url) => {
        const img = new Image();
        img.src = url;
      });
    };
  
    preloadCurrentCharacterGifs();
  }, [char]);
  

  // Cache the initial message text for reuse
  const cachedInitialMessageRef = useRef(initialMessage || "Message from System: If the user hasn't started any of the questions yet please greet them and immediately start the lesson, otherwise tell them welcome back and get back into the lesson");

  const handleStartButtonClick = (e) => {
    // Prevent default behavior
    e.preventDefault();
    
    // Prevent double-click selection
    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    }
    
    // Only execute if not already started
    if (showStartMessage) {
      setStartButtonSelected(true);
      setShowStartMessage(false);
      
      // Get the cached message
      const startMessage = cachedInitialMessageRef.current;
      
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        // Send the cached message when user clicks start
        handleSendMessage(startMessage);
        if (isTTSEnabled) console.log("TTS Start: ", startMessage);
      });
    }
  };

  // avatar look mapping
  const gifMap = {
    felix: {
      typing: "https://i.imgur.com/YwnOomT.gif",
      idle: "https://i.imgur.com/FHXwxRQ.gif",
    },
    ivy: {
      typing: "https://i.imgur.com/schgkuL.gif",
      idle: "https://i.imgur.com/2fvE51N.gif",
    },
    ren: {
      typing: "https://i.imgur.com/7WOXRf5.gif",
      idle: "https://i.imgur.com/ibgqHAY.gif",
    },
    gold: {
      typing: "https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExcGYxdWZmcXMwc2Fmdzh1bDdiN214b2Y2Y2YxZW83NHFpMzBoNjd5OCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/9T5WYktU5aZm1AZLBe/giphy.gif",
      idle: "https://media3.giphy.com/media/v1.Y2lkPTc5MGI3NjExM3J6MzRsdWUwZHlraWR4eW84bWNtamVucWt0bWZmcTNocDU3Y2w2ciZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/vfbdoQLq6XNzWbHU90/giphy.gif",
    },
    alex:  {                     // ← new character
            typing: "https://i.imgur.com/fz2oI65.gif",
            idle:   "https://i.imgur.com/ybbscus.gif"
          },
    // default Mrs. Sanchez
    default: {
      typing: "https://i.imgur.com/uZjHxuK.gif",
      idle: "https://i.imgur.com/RjCt4hf.gif",
    },
  };

  const chosenChar = gifMap[char] || gifMap["default"];

  // Create a wrapped handler that only calls the original onChange handler when not typing.
  const handleWrappedInputChange = (e) => {
    if (!isTyping) {
      handleInputChange(e);
    }
  };

  return (
    <div
      className="fixed inset-0 flex flex-col"
      style={{ backgroundColor: "#73B9AF", margin: 0, padding: 0 }}
    >
      {/* Top Bar for Icons */}
      <div className="flex items-center justify-between p-4 relative z-10">
        <div className="flex items-center gap-8">
          {/* Dynamic TTS Button */}
          <button
            onClick={handleTTSButtonClick}
            className="p-0 transition duration-300"
            aria-label={isTTSEnabled ? "Mute TTS" : "Unmute TTS"}
          >
            {isTTSEnabled ? <SpeakerOnIcon /> : <SpeakerOffIcon />}
          </button>
        </div>
      </div>

      {/* Avatar and Chat Area */}
      <div
        className="absolute inset-0 w-full h-[50%] flex flex-col overflow-hidden"
        style={{ backgroundColor: "#73B9AF" }}
      >
        <div
          className="absolute top-0 left-0 w-full h-full flex items-center justify-center"
          style={{ alignSelf: "center" }}
        >
          <div
            style={{
              height: "100%",
              overflow: "hidden",
              paddingTop: "12px",
              paddingBottom: "12px",
            }}
          >
            <img
              src={isTyping ? chosenChar.typing : chosenChar.idle}
              alt="Avatar Animation"
              style={{
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        </div>
      </div>

      {/* Bottom Section: Chat Area and Buttons */}
      <div
        className="absolute bottom-0 w-full flex flex-col items-center justify-start p-8 z-10"
        style={{ backgroundColor: "#ECECEC", height: "50%" }}
      >
        <div
          ref={messagesContainerRef}
          style={{
            maxHeight: "1000px",
            overflowY: "auto",
            width: "100%",
            padding: "8px",
          }}
        >
          {/* Displayed Text */}
          {displayedText && (
            <div
              className="p-4 bg-[#73B9AF] text-black text-lg md:text-xl rounded-lg shadow-md break-words cursor-pointer"
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "20px",
                fontWeight: "500",
                lineHeight: "normal",
                color: "#000",
                textAlign: "center",
                background: "#73B9AF",
                backdropFilter: "blur(30px)",
                marginBottom: "16px",
                width: "100%",
              }}
              onMouseEnter={() => {
                if (!isTyping && speakHoverText) {
                  speakHoverText(modifiedText);
                }
              }}
              //onClick={handleSkipTyping}
            >
              <span>{modifiedText}</span>
            </div>
          )}

          {/* Voice Input and Buttons Section */}
          <div
            className="flex flex-col items-center justify-center"
            style={{ width: "100%", marginBottom: "16px" }}
          >
            {showStartMessage ? (
              <div className="flex flex-col items-center">
                <div
                  className="p-4 bg-[#73B9AF] text-black text-lg md:text-xl rounded-lg shadow-md break-words cursor-pointer"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "20px",
                    fontWeight: "500",
                    lineHeight: "normal",
                    color: "#000",
                    textAlign: "center",
                    background: "#73B9AF",
                    backdropFilter: "blur(30px)",
                    marginBottom: "16px",
                    width: "100%",
                  }}
                >
                  <p>
                    Press the "Start" button to begin chatting! Once you start, you can
                    type your questions or click the buttons that pop up to choose an
                    answer. Click the speaker button to mute or hear the bot.
                  </p>
                </div>
                <button
                onClick={handleStartButtonClick}
                onMouseDown={(e) => e.preventDefault()} // Prevent text selection on double-click
                className={`${
                  startButtonSelected
                    ? "bg-[#73B9AF]"
                    : "bg-white hover:bg-[#AFD3CD] active:bg-[#73B9AF]"
                }`}
                style={{
                  margin: "0 24px",
                  minHeight: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "20px",
                  width: "calc(100% - 48px)",
                  userSelect: "none", // Prevent text selection
                }}
              >
                Start
              </button>
              </div>
            ) : (
              Object.keys(options).length > 0 && (
                <div
                  className="flex flex-wrap w-full"
                  style={{
                    justifyContent: "center",
                    alignItems: "stretch",
                    marginBottom: "16px",
                  }}
                >
                  {Object.entries(options).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedOptionKey(key);
                        handleActivityButtonClick(value);
                      }}
                      onMouseEnter={() => {
                        if (!isTyping && speakHoverText) {
                          speakHoverText(value);
                        }
                      }}
                      className={`${
                        selectedOptionKey === key
                          ? "bg-[#73B9AF]"
                          : "bg-white hover:bg-[#AFD3CD] active:bg-[#73B9AF]"
                      }`}
                      style={{
                        flex: "1 0 30%",
                        margin: "8px",
                        minHeight: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        fontWeight: "500",
                        fontSize: "20px",
                      }}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              )
            )}
          </div>
        </div>

        {/* Input and Send Button */}
        <div
          className="flex items-center w-full relative"
          style={{
            position: "fixed",
            bottom: "32px",
            left: "24px",
            right: "24px",
            maxWidth: "calc(100% - 48px)",
          }}
        >
          <input
          ref={inputRef}
          value={question}
          onChange={handleWrappedInputChange}  // Use the wrapped handler
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          className="flex-grow h-12 pl-12 pr-10 rounded-full text-xl focus:outline-none transition duration-300"
          type="text"
          placeholder={placeholderText}
          disabled={showStartMessage}
          readOnly={isTyping} // Input remains unchanged when typing is happening
          style={{ color: "#333", backgroundColor: "#FFFFFF" }}
        />
          {/* Voice Input Button inside the input box */}
          <button
            onClick={handleSafeVoiceInput} // Use the safe wrapper
            className={`absolute left-3 inset-y-0 my-auto h-8 w-8 flex items-center justify-center rounded-full bg-transparent ${
              shouldAllowVoiceInput ? "text-black" : "text-gray-400"
            } ${
              isListening ? "pulsating" : ""
            }`}
            aria-label="Voice Input"
            style={{ 
              cursor: shouldAllowVoiceInput ? "pointer" : "not-allowed" 
            }}
            disabled={!shouldAllowVoiceInput}
          >
            <FontAwesomeIcon
              icon={isListening ? faMicrophone : faMicrophoneSlash}
              style={{ 
                color: shouldAllowVoiceInput ? "#000" : "#999", 
                fontSize: "16px", 
                lineHeight: "16px" 
              }}
              className="fa-fw"
            />
          </button>

          {/* Send / Skip Button */}
          <button
            onClick={
              isLoading
                ? null
                : isTyping && messageReceived
                ? handleFastForward
                : handleInputSendMessage
            }
            className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center"
            disabled={
              isLoading ||
              (isTyping && !messageReceived) ||
              (!question.trim() && !isTyping) ||
              showStartMessage
            }
            aria-label={isTyping && messageReceived ? "Skip Typing" : "Send Message"}
          >
            {isLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : isTyping && messageReceived ? (
              <FontAwesomeIcon icon={faForwardStep} style={{ fontSize: "24px" }} />
            ) : (
              <SendChatIcon />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LMSActivityStyle;